import { IconLayer } from '@deck.gl/layers';
import type { MapStationFeature, SubwayStationFeature } from '@vitals/shared';

export type StationGlyphKey =
  | 'local' | 'local-terminal' | 'local-transfer' | 'local-transfer-terminal'
  | 'express' | 'express-terminal' | 'express-transfer' | 'express-transfer-terminal';

const GLYPHS: readonly StationGlyphKey[] = [
  'local', 'local-terminal', 'local-transfer', 'local-transfer-terminal',
  'express', 'express-terminal', 'express-transfer', 'express-transfer-terminal',
];
const CELL_WIDTH = 24;
const CELL_HEIGHT = 24;
export const LOCAL_GLYPH_VISIBLE_SIZE = 18;
export const STATION_MARKER_MIN_PIXELS = 12;
export const STATION_MARKER_MAX_PIXELS = 22;

const glyphSvg = (key: StationGlyphKey, x: number): string => {
  const express = key.startsWith('express');
  const transfer = key.includes('transfer');
  const terminal = key.endsWith('terminal');
  const width = express ? 22 : transfer ? 20 : LOCAL_GLYPH_VISIBLE_SIZE;
  const height = transfer ? 20 : LOCAL_GLYPH_VISIBLE_SIZE;
  const left = x + (CELL_WIDTH - width) / 2;
  const top = (CELL_HEIGHT - height) / 2;
  const radius = height / 2;
  const outerStroke = transfer ? 2 : 2.5;
  const inner = transfer
    ? `<rect x="${left + 4}" y="${top + 4}" width="${width - 8}" height="${height - 8}" rx="${radius - 4}" fill="none" stroke="#080c14" stroke-width="1.5"/>`
    : '';
  const cap = terminal
    ? `<rect x="${left + width - 4}" y="${top + 3}" width="2.5" height="${height - 6}" rx="1.25" fill="#080c14"/>`
    : '';
  return `<g><rect x="${left}" y="${top}" width="${width}" height="${height}" rx="${radius}" fill="#e8eefc" stroke="#080c14" stroke-width="${outerStroke}"/>${inner}${cap}</g>`;
};

const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${CELL_WIDTH * GLYPHS.length}" height="${CELL_HEIGHT}" viewBox="0 0 ${CELL_WIDTH * GLYPHS.length} ${CELL_HEIGHT}">${GLYPHS.map((key, index) => glyphSvg(key, index * CELL_WIDTH)).join('')}</svg>`;
export const STATION_GLYPH_ATLAS = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
export const STATION_GLYPH_MAPPING = Object.fromEntries(GLYPHS.map((key, index) => [
  key,
  { x: index * CELL_WIDTH, y: 0, width: CELL_WIDTH, height: CELL_HEIGHT, anchorX: CELL_WIDTH / 2, anchorY: CELL_HEIGHT / 2, mask: false },
]));

export function stationGlyphKey(
  station: Pick<SubwayStationFeature['properties'], 'shape' | 'transfer' | 'terminal'>,
): StationGlyphKey {
  return [
    station.shape,
    station.transfer ? 'transfer' : '',
    station.terminal ? 'terminal' : '',
  ].filter(Boolean).join('-') as StationGlyphKey;
}

const commonProps = {
  iconAtlas: STATION_GLYPH_ATLAS,
  iconMapping: STATION_GLYPH_MAPPING,
  getSize: 52,
  sizeUnits: 'meters' as const,
  sizeMinPixels: STATION_MARKER_MIN_PIXELS,
  sizeMaxPixels: STATION_MARKER_MAX_PIXELS,
  pickable: true,
};

export function buildSubwayStationLayer(
  id: string,
  data: SubwayStationFeature[],
): IconLayer<SubwayStationFeature> {
  return new IconLayer<SubwayStationFeature>({
    id,
    data,
    ...commonProps,
    getPosition: (feature) => feature.geometry.coordinates,
    getIcon: (feature) => stationGlyphKey(feature.properties),
  });
}

export function buildCommuterStationLayer(
  id: string,
  data: MapStationFeature[],
): IconLayer<MapStationFeature> {
  return new IconLayer<MapStationFeature>({
    id,
    data,
    ...commonProps,
    getPosition: (feature) => feature.geometry.coordinates,
    getIcon: () => 'local',
  });
}
